import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Calendar, Clock, Share2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { allBlogArticles } from "../data/blogArticles";
import GuideLeadForm from "../components/GuideLeadForm";
import { Link } from "react-router-dom";

const BlogArticle = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [articleId]);

  // Convert markdown content to HTML for articles from blogArticles.js
  const convertMarkdownToHtml = (markdown) => {
    if (!markdown) return '';
    // tables first
    let html = markdown.replace(/((?:^\|.*\|[ \t]*\r?\n?)+)/gim, (block) => {
      const lines = block.trim().split('\n').map((l) => l.trim()).filter(Boolean);
      if (lines.length < 2) return block;
      const rows = lines.filter((l) => !/^\|[\s:|-]+\|$/.test(l));
      const cells = (l) => l.split('|').slice(1, -1).map((c) => c.trim());
      const head = cells(rows[0]).map((c) => `<th>${c}</th>`).join('');
      const body = rows.slice(1).map((r) => `<tr>${cells(r).map((c) => `<td>${c}</td>`).join('')}</tr>`).join('');
      return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>\n`;
    });
    return html
      .replace(/^#### (.*$)/gim, '<h4>$1</h4>')
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2">$1</a>')
      .replace(/\*\*([^*]+)\*\*/gim, '<strong>$1</strong>')
      .replace(/(^|[^*<\w])\*([^*\n]+)\*(?!\*)/gim, '$1<em>$2</em>')
      .replace(/^\- \[ \] (.*$)/gim, '<li>☐ $1</li>')
      .replace(/^\- (.*$)/gim, '<li>$1</li>')
      .replace(/^\d+\. (.*$)/gim, '<li>$1</li>')
      .replace(/\n\n/gim, '</p><p>')
      .replace(/\n/gim, '<br>')
      .replace(/<li>/g, '</ul><ul><li>')
      .replace(/<\/li><\/ul><ul>/g, '</li>')
      .replace(/^<\/ul>/, '')
      .replace(/<ul><li>/, '<ul><li>');
  };

  // Get article from blogArticles.js if not in hardcoded list
  const dynamicArticle = useMemo(() => {
    const found = allBlogArticles.find(a => a.id === articleId);
    if (found && found.content) {
      return {
        title: found.title,
        date: found.date,
        readTime: found.readTime,
        category: found.category,
        content: convertMarkdownToHtml(found.content)
      };
    }
    return null;
  }, [articleId]);

  const articles = {
    "myntra-onboarding-guide-2026": {
      title: "Complete Guide to Myntra Seller Onboarding 2026",
      date: "March 15, 2026",
      readTime: "8 min read",
      category: "Platform Guides",
      content: `
        <h2>Why Sell on Myntra?</h2>
        <p>Myntra is India's leading fashion e-commerce platform with over 50 million active users. As a Myntra seller, you get access to a massive customer base actively looking for fashion, beauty, and lifestyle products.</p>

        <h2>Myntra Seller Onboarding Requirements</h2>
        <h3>Essential Documents:</h3>
        <ul>
          <li><strong>GST Registration Certificate</strong> - Mandatory for all sellers</li>
          <li><strong>PAN Card</strong> - For business verification</li>
          <li><strong>Bank Account Details</strong> - For payment settlements</li>
          <li><strong>Brand Authorization</strong> - If selling branded products</li>
          <li><strong>Product Catalog</strong> - With high-quality images and descriptions</li>
          <li><strong>Trademark Certificate</strong> - For brand owners</li>
        </ul>

        <h2>Myntra Onboarding Process: Step-by-Step</h2>
        <h3>Step 1: Registration</h3>
        <p>Visit the Myntra Seller Portal and fill out the registration form with your business details, contact information, and product categories you plan to sell.</p>

        <h3>Step 2: Document Verification</h3>
        <p>Upload all required documents. Myntra's team will verify your documents within 3-5 business days. Ensure all documents are clear and valid.</p>

        <h3>Step 3: Product Cataloging</h3>
        <p>Create your product catalog with detailed descriptions, high-quality images (minimum 1000x1000px), accurate pricing, and inventory counts.</p>

        <h3>Step 4: Quality Check</h3>
        <p>Myntra conducts a quality assessment of your products to ensure they meet platform standards. This includes checking product authenticity and quality.</p>

        <h3>Step 5: Account Activation</h3>
        <p>Once approved, your seller account will be activated, and you can start listing products on Myntra.</p>

        <h2>Myntra Seller Fees & Commission Structure</h2>
        <ul>
          <li><strong>Commission Rates</strong>: 10-30% depending on product category</li>
          <li><strong>Payment Cycle</strong>: Weekly settlements (T+7 days)</li>
          <li><strong>Shipping Costs</strong>: Borne by Myntra for most categories</li>
          <li><strong>Return Handling</strong>: Managed by Myntra logistics</li>
        </ul>

        <h2>Best Practices for Myntra Success</h2>
        <ol>
          <li><strong>High-Quality Product Images</strong> - Use professional photography with multiple angles</li>
          <li><strong>Accurate Descriptions</strong> - Provide detailed size guides, material information, and care instructions</li>
          <li><strong>Competitive Pricing</strong> - Research competitor pricing and offer value</li>
          <li><strong>Fast Dispatch</strong> - Ship orders within 24 hours for better ratings</li>
          <li><strong>Maintain Stock</strong> - Avoid out-of-stock situations to maintain search rankings</li>
        </ol>

        <h2>Common Myntra Onboarding Challenges</h2>
        <p><strong>Document Rejections</strong>: Ensure all documents are valid, clear, and match your business information exactly.</p>
        <p><strong>Product Approval Delays</strong>: Follow Myntra's product listing guidelines carefully. Use high-resolution images and accurate descriptions.</p>
        <p><strong>Brand Authorization</strong>: If selling branded products, obtain proper authorization letters from brand owners.</p>

        <h2>Why Use BrandBorn for Myntra Onboarding?</h2>
        <p>Navigating Myntra's onboarding process can be complex. BrandBorn specializes in Myntra seller onboarding and can help you:</p>
        <ul>
          <li>Complete documentation quickly and accurately</li>
          <li>Ensure first-time approval without rejections</li>
          <li>Optimize product listings for maximum visibility</li>
          <li>Launch your store in 2-4 weeks instead of months</li>
          <li>Provide ongoing account management and support</li>
        </ul>

        <h2>Get Started with Myntra Today</h2>
        <p>Ready to launch your brand on Myntra? Contact BrandBorn for expert Myntra onboarding services. We'll handle the entire process from registration to launch, ensuring your brand gets maximum visibility from day one.</p>
      `
    },
    "nykaa-onboarding-complete-process": {
      title: "How to Onboard Your Beauty Brand on Nykaa - Complete Process",
      date: "March 12, 2026",
      readTime: "7 min read",
      category: "Platform Guides",
      content: `
        <h2>Why Choose Nykaa for Your Beauty Brand?</h2>
        <p>Nykaa is India's #1 beauty and wellness e-commerce platform with over 70 million app downloads. It's the go-to destination for beauty enthusiasts, offering unmatched visibility for beauty, cosmetics, skincare, and wellness brands.</p>

        <h2>Nykaa Seller Requirements</h2>
        <h3>Eligibility Criteria:</h3>
        <ul>
          <li><strong>Product Category</strong>: Beauty, cosmetics, skincare, haircare, wellness, or personal care</li>
          <li><strong>Product Quality</strong>: Authentic products with proper certifications</li>
          <li><strong>FSSAI License</strong>: Required for ingestible products (supplements, vitamins)</li>
          <li><strong>Cosmetic License</strong>: For skincare and cosmetic products</li>
          <li><strong>Brand Ownership/Authorization</strong>: Prove you own the brand or have authorization to sell</li>
        </ul>

        <h2>Nykaa Onboarding Documents Checklist</h2>
        <ul>
          <li>GST Registration Certificate</li>
          <li>PAN Card</li>
          <li>Bank Account Details with cancelled cheque</li>
          <li>FSSAI License (for food/supplement products)</li>
          <li>Cosmetic Manufacturing License</li>
          <li>Product Safety Certifications</li>
          <li>Brand Registration Certificate</li>
          <li>Product Images (high resolution, white background)</li>
          <li>Product Descriptions with ingredients list</li>
        </ul>

        <h2>Nykaa Onboarding Process Timeline</h2>
        <h3>Week 1: Application & Documentation</h3>
        <p>Submit your seller application through Nykaa's vendor portal. Provide all required business documents and product information.</p>

        <h3>Week 2: Product Verification</h3>
        <p>Nykaa's quality team reviews your products, checks authenticity, and verifies all certifications. You may need to send product samples for testing.</p>

        <h3>Week 3: Catalog Creation</h3>
        <p>Create detailed product listings with ingredients, usage instructions, benefits, and high-quality images. Nykaa has strict guidelines for product photography.</p>

        <h3>Week 4: Launch</h3>
        <p>After final approval, your products go live on Nykaa. The platform handles marketing, logistics, and customer service.</p>

        <h2>Nykaa Commission & Fee Structure</h2>
        <ul>
          <li><strong>Commission</strong>: 15-35% depending on product category and brand positioning</li>
          <li><strong>Payment Terms</strong>: Net 30 days settlement</li>
          <li><strong>Logistics</strong>: Fulfilled by Nykaa in most cases</li>
          <li><strong>Marketing Support</strong>: Access to Nykaa's promotional campaigns</li>
        </ul>

        <h2>Nykaa Product Listing Best Practices</h2>
        <ol>
          <li><strong>Professional Photography</strong>: Use high-resolution images (minimum 1500x1500px) with clean white backgrounds</li>
          <li><strong>Detailed Ingredients</strong>: List all ingredients in order of concentration</li>
          <li><strong>Clear Benefits</strong>: Explain what the product does and who it's for</li>
          <li><strong>How to Use</strong>: Provide step-by-step usage instructions</li>
          <li><strong>Certifications</strong>: Display cruelty-free, vegan, organic badges if applicable</li>
        </ol>

        <h2>Why Partner with BrandBorn for Nykaa Onboarding?</h2>
        <p>Nykaa has stringent quality requirements and a detailed approval process. BrandBorn helps beauty brands:</p>
        <ul>
          <li>Navigate complex certification requirements</li>
          <li>Create compelling product listings that convert</li>
          <li>Ensure faster approval (2-3 weeks vs 6-8 weeks)</li>
          <li>Optimize product positioning for maximum sales</li>
          <li>Handle ongoing account management and inventory planning</li>
        </ul>

        <h2>Launch Your Beauty Brand on Nykaa Today</h2>
        <p>Ready to reach millions of beauty enthusiasts? Contact BrandBorn for expert Nykaa onboarding services. We specialize in beauty brand launches and can help you succeed on India's leading beauty platform.</p>
      `
    },
    "quick-commerce-platforms-comparison": {
      title: "Quick Commerce Platforms: Zepto vs Blinkit vs Swiggy Instamart",
      date: "March 8, 2026",
      readTime: "10 min read",
      category: "Quick Commerce",
      content: `
        <h2>What is Quick Commerce?</h2>
        <p>Quick commerce (Q-commerce) delivers products to customers in 10-30 minutes. It's revolutionizing how Indians shop for groceries, personal care, and everyday essentials. For brands, it offers instant visibility and rapid sales growth.</p>

        <h2>Zepto: India's Fastest Growing Quick Commerce</h2>
        <h3>Zepto Overview:</h3>
        <ul>
          <li><strong>Delivery Time</strong>: 10 minutes</li>
          <li><strong>Coverage</strong>: 10+ cities including Mumbai, Bangalore, Delhi, Hyderabad</li>
          <li><strong>Target Audience</strong>: Young, tech-savvy urban consumers</li>
          <li><strong>Best For</strong>: FMCG brands, snacks, beverages, personal care</li>
        </ul>

        <h3>Zepto Seller Benefits:</h3>
        <ul>
          <li>Fastest delivery promise (10 minutes) drives impulse purchases</li>
          <li>Growing rapidly with strong investor backing</li>
          <li>High order frequency - customers order 3-4 times per week</li>
          <li>Excellent for product sampling and new launches</li>
        </ul>

        <h2>Blinkit (formerly Grofers): Market Leader</h2>
        <h3>Blinkit Overview:</h3>
        <ul>
          <li><strong>Delivery Time</strong>: 10-20 minutes</li>
          <li><strong>Coverage</strong>: 25+ cities pan-India</li>
          <li><strong>Zomato Integration</strong>: Backed by Zomato's massive user base</li>
          <li><strong>Best For</strong>: Groceries, fresh produce, dairy, staples</li>
        </ul>

        <h3>Blinkit Seller Advantages:</h3>
        <ul>
          <li>Largest dark store network in India (500+ locations)</li>
          <li>Integration with Zomato brings 70M+ potential customers</li>
          <li>Strong focus on fresh and perishable categories</li>
          <li>Established brand with high customer trust</li>
        </ul>

        <h2>Swiggy Instamart: Backed by Swiggy's Success</h2>
        <h3>Swiggy Instamart Overview:</h3>
        <ul>
          <li><strong>Delivery Time</strong>: 15-30 minutes</li>
          <li><strong>Coverage</strong>: 20+ cities with rapid expansion</li>
          <li><strong>Swiggy Integration</strong>: Access to Swiggy's 200M+ app users</li>
          <li><strong>Best For</strong>: Packaged foods, beverages, snacks, essentials</li>
        </ul>

        <h3>Swiggy Instamart Benefits:</h3>
        <ul>
          <li>Massive existing user base from Swiggy food delivery</li>
          <li>Strong logistics and technology infrastructure</li>
          <li>Cross-promotion opportunities with Swiggy restaurant partners</li>
          <li>Premium positioning attracts affluent customers</li>
        </ul>

        <h2>Commission & Fee Comparison</h2>
        <table>
          <tr>
            <th>Platform</th>
            <th>Commission</th>
            <th>Payment Terms</th>
            <th>Onboarding Fee</th>
          </tr>
          <tr>
            <td>Zepto</td>
            <td>15-25%</td>
            <td>Net 15 days</td>
            <td>No fee</td>
          </tr>
          <tr>
            <td>Blinkit</td>
            <td>18-28%</td>
            <td>Net 21 days</td>
            <td>No fee</td>
          </tr>
          <tr>
            <td>Swiggy Instamart</td>
            <td>20-30%</td>
            <td>Net 30 days</td>
            <td>No fee</td>
          </tr>
        </table>

        <h2>Which Quick Commerce Platform is Best for Your Brand?</h2>
        
        <h3>Choose Zepto if:</h3>
        <ul>
          <li>You're targeting young, urban millennials and Gen Z</li>
          <li>Your products are impulse-buy categories (snacks, beverages)</li>
          <li>You want the fastest delivery promise (10 minutes)</li>
          <li>You're launching in metro cities first</li>
        </ul>

        <h3>Choose Blinkit if:</h3>
        <ul>
          <li>You need maximum geographic coverage across India</li>
          <li>Your products are fresh/perishable (fruits, vegetables, dairy)</li>
          <li>You want access to Zomato's massive user base</li>
          <li>You need the largest dark store network</li>
        </ul>

        <h3>Choose Swiggy Instamart if:</h3>
        <ul>
          <li>You want access to Swiggy's 200M+ existing users</li>
          <li>Your brand targets premium/affluent customers</li>
          <li>You're in packaged foods, snacks, or beverage categories</li>
          <li>You want cross-promotion with food delivery</li>
        </ul>

        <h2>Multi-Platform Strategy (Recommended)</h2>
        <p>Most successful brands launch on all three platforms simultaneously to maximize reach. Each platform has unique customer demographics and shopping patterns.</p>

        <h2>Quick Commerce Onboarding with BrandBorn</h2>
        <p>BrandBorn specializes in quick commerce onboarding across Zepto, Blinkit, and Swiggy Instamart. We help you:</p>
        <ul>
          <li>Launch on multiple platforms simultaneously</li>
          <li>Optimize product assortment for each platform</li>
          <li>Set up dark store inventory management</li>
          <li>Navigate platform-specific requirements</li>
          <li>Complete onboarding in 1-2 weeks (vs 4-6 weeks DIY)</li>
        </ul>

        <h2>Get Started with Quick Commerce Today</h2>
        <p>Ready to launch on India's fastest-growing commerce channel? Contact BrandBorn for expert quick commerce onboarding. We'll help you go live on Zepto, Blinkit, and Swiggy Instamart quickly and efficiently.</p>
      `
    }
  };

  // Programmatic SEO articles: fetch from backend when not found locally
  const [fetchedArticle, setFetchedArticle] = useState(null);
  const [fetchState, setFetchState] = useState("idle");
  const hasLocal = !!(dynamicArticle || articles[articleId]);
  useEffect(() => {
    setFetchedArticle(null);
    if (hasLocal) { setFetchState("idle"); return; }
    setFetchState("loading");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo/blogs/${articleId}`)
      .then((r) => { if (!r.ok) throw new Error("nf"); return r.json(); })
      .then((d) => {
        setFetchedArticle({
          title: d.title, date: d.date, readTime: d.readTime, category: d.category,
          keywords: d.keywords, metaDescription: d.metaDescription,
          lang: d.lang, altSlug: d.altSlug, platform: d.platform, city: d.city,
          rawContent: d.content, content: convertMarkdownToHtml(d.content),
        });
        setFetchState("done");
      })
      .catch(() => setFetchState("notfound"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId, hasLocal]);

  // Use dynamic article from blogArticles.js if available, then hardcoded, then backend-generated
  const article = dynamicArticle || articles[articleId] || fetchedArticle;

  // Set document title as fallback for Helmet
  useEffect(() => {
    if (article?.title) {
      document.title = `${article.title} | BrandBorn - Best Onboarding Agency India`;
    }
    return () => {
      document.title = 'Best Onboarding Agency in India | BrandBorn - 98% Approval Rate';
    };
  }, [article]);

  // Generate meta description from content (strip HTML, take first 160 chars)
  const metaDescription = useMemo(() => {
    if (article?.metaDescription) return article.metaDescription;
    if (!article?.content) return "BrandBorn - India's #1 Marketplace Onboarding Agency. Read expert guides on selling on Myntra, Nykaa, Ajio, Blinkit, and more.";
    const stripped = article.content.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    return stripped.substring(0, 155) + '...';
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center pt-20">
        {fetchState === "notfound" ? (
          <div className="text-center" data-testid="article-not-found">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Article not found</h1>
            <button onClick={() => navigate('/blog')} className="bg-red-500 text-white px-6 py-3 rounded-full font-semibold hover:bg-red-600 transition" data-testid="browse-all-guides-btn">Browse All Guides</button>
          </div>
        ) : (
          <div className="text-gray-500 animate-pulse" data-testid="article-loading">Loading article…</div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Dynamic SEO Meta Tags */}
      <Helmet>
        <title>{article.title} | BrandBorn - Best Onboarding Agency India</title>
        <meta name="description" content={metaDescription} />
        {article.keywords?.length > 0 && <meta name="keywords" content={article.keywords.join(", ")} />}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <link rel="canonical" href={`https://www.brandborn.in/blog/${articleId}`} />
        <meta property="og:title" content={`${article.title} | BrandBorn`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={`https://www.brandborn.in/blog/${articleId}`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="BrandBorn" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${article.title} | BrandBorn`} />
        <meta name="twitter:description" content={metaDescription} />
        {article.altSlug && <link rel="alternate" hrefLang={article.lang === "hi" ? "en-IN" : "hi-IN"} href={`https://www.brandborn.in/blog/${article.altSlug}`} />}
        {article.altSlug && <link rel="alternate" hrefLang={article.lang === "hi" ? "hi-IN" : "en-IN"} href={`https://www.brandborn.in/blog/${articleId}`} />}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": metaDescription,
          "inLanguage": article.lang === "hi" ? "hi-IN" : "en-IN",
          "author": {
            "@type": "Person",
            "name": "Anmol Singh",
            "jobTitle": "Founder & CEO, BrandBorn",
            "url": "https://www.brandborn.in/about",
            "image": "https://www.brandborn.in/images/team/ceo.jpeg"
          },
          "publisher": { "@type": "Organization", "name": "BrandBorn", "url": "https://www.brandborn.in", "logo": { "@type": "ImageObject", "url": "https://www.brandborn.in/images/logos/brandborn.png" }},
          "datePublished": article.date,
          "dateModified": article.date,
          "mainEntityOfPage": `https://www.brandborn.in/blog/${articleId}`
        })}</script>
        {(() => {
          const faqs = [...(article.rawContent || article.content).matchAll(/\*\*Q:\s*(.+?)\*\*\s*\nA:\s*(.+)/g)].map((m) => ({
            "@type": "Question", "name": m[1].trim(),
            "acceptedAnswer": { "@type": "Answer", "text": m[2].replace(/\*\*/g, "").replace(/\[([^\]]+)\]\([^)]+\)/g, "$1").trim() },
          }));
          return faqs.length > 0 ? (
            <script type="application/ld+json">{JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": faqs })}</script>
          ) : null;
        })()}
      </Helmet>

      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="/images/team/author.jpeg" 
                alt="BrandBorn Logo" 
                className="h-10 w-10 md:h-12 md:w-12"
              />
              <span className="text-xl md:text-2xl font-bold text-gray-900">BrandBorn</span>
            </div>
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-700 hover:text-red-500 transition"
            >
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Back to Home</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Article Content */}
      <article className="pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Article Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div className="text-red-500 font-semibold">{article.category}</div>
              {article.altSlug && (
                <Link to={`/blog/${article.altSlug}`} data-testid="lang-toggle-link"
                  className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full border border-red-300 text-red-600 text-sm font-semibold hover:bg-red-50 transition">
                  {article.lang === "hi" ? "Read in English" : "हिंदी में पढ़ें"}
                </Link>
              )}
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {article.title}
            </h1>
            <div className="flex items-center gap-6 text-gray-600 mb-6">
              <div className="flex items-center gap-2">
                <Calendar size={18} />
                <span>{article.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={18} />
                <span>{article.readTime}</span>
              </div>
              <button className="flex items-center gap-2 hover:text-red-500 transition">
                <Share2 size={18} />
                <span className="hidden sm:inline">Share</span>
              </button>
            </div>
          </div>

          {/* Article Body */}
          <div 
            className="blog-article-content prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Lead capture for generated guides */}
          {fetchedArticle && article === fetchedArticle ? (
            <GuideLeadForm platform={article.platform} city={article.city} slug={articleId} lang={article.lang} />
          ) : (
          <div className="mt-12 p-8 bg-gradient-to-r from-red-50 to-red-100 rounded-2xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Need Help with E-commerce Onboarding?
            </h3>
            <p className="text-gray-700 mb-6">
              BrandBorn specializes in onboarding brands on Myntra, Nykaa, Ajio, Zepto, Blinkit, and Swiggy Instamart. 
              We handle everything from documentation to launch in just 2-4 weeks.
            </p>
            <button 
              onClick={() => navigate('/#contact')}
              className="bg-red-500 text-white px-8 py-3 rounded-full hover:bg-red-600 transition font-semibold"
            >
              Get Started Today
            </button>
          </div>
          )}
        </div>
      </article>
    </div>
  );
};

export default BlogArticle;